import styled from 'styled-components';

export const TrainingSessionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
  width: 100%;
`;

export const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

export const PageTitle = styled.h1`
  font-size: 2.1rem;
  color: var(--accent-orange);
  font-family: 'Poppins', sans-serif;
  font-weight: 900;
  margin-bottom: 0.3rem;
  text-align: left;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: var(--mobile-headline, 1.5rem);
  }
`;

export const PageSubtitle = styled.p`
  font-size: 1rem;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  margin-top: 0;
  margin-bottom: 1.1rem;
  text-align: left;

  @media (max-width: 768px) {
    font-size: var(--mobile-paragraph, 0.75rem);
  }
`;

/** Wrapper for add-jump / add-ground-training form areas – grey background, rounded corners */
export const FormAreaWrapper = styled.div`
  background: var(--bg-sidebar);
  border-radius: 1rem;
  padding: 1.5rem;
  margin-bottom: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 0.75rem;
  }
`;

export const FormSection = styled.section`
  margin-bottom: 1.2rem;

  @media (max-width: 768px) {
    width: 100%;
    min-width: 0;
    padding-left: 1rem;
    padding-right: 1rem;
    box-sizing: border-box;

    form {
      width: 100%;
      min-width: 0;
    }
  }
`;

export const FormTitle = styled.h2`
  font-size: 1.4rem;
  color: var(--accent-orange);
  font-family: 'Poppins', sans-serif;
  font-weight: 800;
  margin-bottom: 2rem;
  text-align: left;
  position: relative;
  padding-left: 1rem;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: var(--mobile-section-title, 1rem);
    margin-bottom: 1rem;
  }
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 6px;
    height: 100%;
    background: var(--accent-orange);
  }
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.7rem 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr !important;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
    width: 100%;
  }
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.2rem;

  @media (max-width: 768px) {
    align-items: stretch;
    width: 100%;
    min-width: 0;
    grid-column: 1 / -1; /* full width in single-column grid */
  }
`;

export const Label = styled.label`
  font-size: 1rem;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    font-size: 0.75rem;
    margin-bottom: 0.3rem;
    text-align: center;
    width: 100%;
  }
`;

/** Bright grey for empty/placeholder state – ensures visibility on desktop and mobile (iOS respects -webkit-text-fill-color) */
export const INPUT_EMPTY_PLACEHOLDER_COLOR = '#b0b0b0';

export const Input = styled.input`
  padding: 0.4rem 0.7rem;
  font-size: 1rem;
  border-radius: 0.5rem;
  border: 1px solid #e0e6ed;
  font-family: 'Poppins', sans-serif;
  background: #fff;
  width: 100%;
  box-sizing: border-box;
  color: var(--text-gray);
  appearance: none;
  -webkit-appearance: none;
  min-height: 2.5rem;

  &::placeholder {
    color: ${INPUT_EMPTY_PLACEHOLDER_COLOR};
    opacity: 1;
  }

  @media (max-width: 768px) {
    font-size: 16px !important; /* prevents iOS zoom on focus - must override inline styles */
    padding: 0.5rem 0.7rem;
    text-align: center;
    width: 100% !important;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
    appearance: none;
    -webkit-appearance: none;
    color: var(--text-gray);
    -webkit-text-fill-color: var(--text-gray);
    min-height: 2.75rem;

    &::placeholder {
      color: ${INPUT_EMPTY_PLACEHOLDER_COLOR};
      opacity: 1;
    }
  }
`;

export const Select = styled.select`
  padding: 0.4rem 0.7rem;
  font-size: 1rem;
  border-radius: 0.5rem;
  border: 1px solid #e0e6ed;
  font-family: 'Poppins', sans-serif;
  background: #fff;
  width: 100%;
  box-sizing: border-box;
  color: var(--text-gray);
  appearance: none;
  -webkit-appearance: none;
  min-height: 2.5rem;

  @media (max-width: 768px) {
    font-size: 16px !important;
    padding: 0.5rem 0.7rem;
    text-align: center;
    text-align-last: center;
    width: 100% !important;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
    appearance: none;
    -webkit-appearance: none;
    color: var(--text-gray);
    -webkit-text-fill-color: var(--text-gray);
    min-height: 2.75rem;
  }
`;

export const TelemarkToggle = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

export const ToggleButton = styled.button<{ active?: boolean }>`
  padding: 0.5rem 1.25rem;
  min-width: 4rem;
  border-radius: 0.5rem;
  border: 2px solid ${props => props.active ? 'var(--accent-orange)' : '#e0e6ed'};
  background: ${props => props.active ? 'var(--accent-orange)' : '#f5f5f5'};
  color: ${props => props.active ? '#fff' : 'var(--text-gray)'};
  font-family: 'Poppins', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: var(--accent-orange-light);
    color: #fff;
    border-color: var(--accent-orange);
  }
`;

export const SubmitButton = styled.button`
  margin-top: 0.7rem;
  padding: 0.4rem 1rem;
  border-radius: 0.5rem;
  border: 2px solid var(--accent-orange);
  background: var(--accent-orange);
  color: #fff;
  font-family: 'Poppins', sans-serif;
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: -0.02em;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  white-space: nowrap;

  @media (max-width: 768px) {
    padding: 0.4rem 1rem;
  }

  &:hover, &:focus {
    background: var(--accent-orange-dark);
    border-color: var(--accent-orange-dark);
    color: #fff;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const RecordsSection = styled.div`
  margin-top: 0;
  background: var(--bg-sidebar);
  border-radius: 1.0rem;
  padding: 1.5rem;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    max-width: 100%;
    margin-left: 0;
    margin-right: 0;
    border-radius: 0;
    padding: 1rem;
  }
`;

export const RecordsTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-top: 0;
  background: none;
`;

export const TableHeader = styled.th`
  text-align: left;
  padding: 1.1rem 0.7rem 1.1rem 0;
  color: var(--text-gray);
  font-size: 1.05rem;
  font-family: 'Poppins', sans-serif;
  font-weight: 800;
  border-bottom: 2px solid rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    font-size: var(--mobile-list-header, 0.6rem);
    padding: 0.25rem 0.4rem 0.25rem 0;
  }
`;

export const TableCell = styled.td`
  padding: 0.9rem 0.7rem 0.9rem 0;
  color: var(--text-gray) !important;
  font-family: 'Poppins', sans-serif;
  font-size: 1.05rem;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: var(--mobile-list-cell, 0.65rem);
    padding: 0.25rem 0.4rem 0.25rem 0;
  }
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
`;

export const TableRow = styled.tr`
  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }
`; 

export const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

export const MetricCard = styled.div`
  background: linear-gradient(145deg, rgba(17, 20, 32, 0.95), rgba(23, 27, 42, 0.9));
  border-radius: 1rem;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.12);
`;

export const MetricLabel = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

export const MetricValue = styled.span`
  font-size: 1.9rem;
  font-weight: 900;
  color: var(--accent-orange);
  letter-spacing: -0.02em;
  text-shadow: 0 6px 14px rgba(0, 0, 0, 0.35);
`;

export const MetricDelta = styled.span<{ positive?: boolean }>`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${props => (props.positive ? '#4CAF50' : 'rgba(255, 255, 255, 0.65)')};
  opacity: 0.95;
`;

export const MonthDividerRow = styled.tr`
  background: rgba(255, 255, 255, 0.02);

  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }
`;

export const MonthDividerCell = styled.td`
  padding: 0.8rem 0.5rem 0.35rem;
  border-bottom: none;
`;

export const MonthHeader = styled.div`
  display: flex;
  align-items: center;
  font-family: 'Poppins', sans-serif;
`;

export const MonthLabel = styled.span`
  font-size: 0.85rem;
  font-weight: 800;
  color: var(--text-gray);
  letter-spacing: -0.02em;
`;

export const MonthCount = styled.span`
  font-size: 0.8rem;
  color: var(--text-gray);
  font-weight: 600;
`;

export const EmptyStateMessage = styled.div`
  padding: 1.2rem;
  text-align: center;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  border: 1px dashed rgba(255, 255, 255, 0.2);
  border-radius: 0.8rem;
`;